import React from "react";
import {
    DarwinTrip,
    DarwinFareClass,
    DarwinFare,
    DarwinPtcFare,
} from "../../types/darwin";
import {
    formatDate,
    formatTime,
    formatDuration,
    getLowestFare,
    AERO_AIRPORTS,
} from "../../services/darwinApi";

interface AeroFlightCardProps {
    trip: DarwinTrip;
    isExpanded: boolean;
    onClick: () => void;
    onSelect: (
        fareClass: DarwinFareClass,
        fare: DarwinFare,
        ptcFare: DarwinPtcFare,
    ) => void;
}

const FARE_POLICY =
    "Fully refundable to credit up to 21 days before departure, or refundable to cash with a 3% transactional fee. Cancellations within 21 days incur a 15% fee (refunded as credit), and within 72 hours a 50% fee. Nonrefundable within 12 hours of departure.";

/* Airplane icon — 20x20, fill #757575 */
const PlaneIcon: React.FC = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="#757575">
        <path d="M30.6 18h-9.4L14 6h7l9.6 12ZM31 29h10.5a3.5 3.5 0 1 0 0-7H15l-5-7H4l5 10.5L4 36h6l5-7h7l-8 13h7l10-13Z" />
    </svg>
);

/* Timeline dot — 8px circle */
const TimelineDot: React.FC = () => (
    <div
        style={{
            width: 8,
            height: 8,
            borderRadius: 16,
            backgroundColor: "#e9e9e9",
            flexShrink: 0,
        }}
    />
);

/* Collapsed flight card content — shared between collapsed and expanded states */
const CardContent: React.FC<{
    dateStr: string;
    priceStr: string;
    depTime: string;
    arrTime: string;
    duration: string;
    originCity: string;
    originCode: string;
    destCity: string;
    destCode: string;
    arrivalDateAdjustor: number;
    showSeatsLeft: boolean;
    seatsLeft: number | null;
}> = ({
    dateStr,
    priceStr,
    depTime,
    arrTime,
    duration,
    originCity,
    originCode,
    destCity,
    destCode,
    arrivalDateAdjustor,
    showSeatsLeft,
    seatsLeft,
}) => (
    <>
        {/* Row 1: Date + seats badge + price */}
        <div
            className="flex items-baseline justify-between"
            style={{ marginBottom: 16 }}
        >
            <div className="flex items-baseline gap-3">
                <span
                    style={{
                        fontSize: 28,
                        fontWeight: 400,
                        lineHeight: "32px",
                        color: "#000",
                    }}
                >
                    {dateStr}
                </span>
                {showSeatsLeft && (
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#E53E3E",
                            border: "1px solid #E53E3E",
                            borderRadius: 4,
                            padding: "2px 6px",
                        }}
                    >
                        {seatsLeft} Seats Left
                    </span>
                )}
            </div>
            <span
                style={{
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: "16px",
                    color: "#000",
                }}
            >
                {priceStr}
            </span>
        </div>

        {/* Row 2: Timeline */}
        <div className="flex items-center">
            <div style={{ flexShrink: 0 }}>
                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#000",
                    }}
                >
                    {depTime}
                </div>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: "12px",
                        marginTop: 4,
                    }}
                >
                    <span style={{ color: "#949494" }}>{originCity}</span>{" "}
                    <span style={{ color: "#c6c6c6" }}>{originCode}</span>
                </div>
            </div>
            <div
                className="flex-1 flex items-center justify-center"
                style={{ margin: "0 8px" }}
            >
                <TimelineDot />
                <div
                    style={{ flex: 1, height: 1, backgroundColor: "#e9e9e9" }}
                />
                <div
                    className="flex flex-col items-center"
                    style={{ flexShrink: 0, margin: "0 2px" }}
                >
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: "12px",
                            color: "#c6c6c6",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {duration}
                    </span>
                    <PlaneIcon />
                </div>
                <div
                    style={{ flex: 1, height: 1, backgroundColor: "#e9e9e9" }}
                />
                <TimelineDot />
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div
                    style={{
                        fontSize: 20,
                        fontWeight: 400,
                        lineHeight: "24px",
                        color: "#000",
                    }}
                >
                    {arrTime}
                    {arrivalDateAdjustor > 0 && (
                        <sup
                            style={{
                                fontSize: 12,
                                color: "#fb6900",
                                marginLeft: 2,
                            }}
                        >
                            +{arrivalDateAdjustor}
                        </sup>
                    )}
                </div>
                <div
                    style={{
                        fontSize: 12,
                        fontWeight: 500,
                        lineHeight: "12px",
                        marginTop: 4,
                    }}
                >
                    <span style={{ color: "#949494" }}>{destCity}</span>{" "}
                    <span style={{ color: "#c6c6c6" }}>{destCode}</span>
                </div>
            </div>
        </div>
    </>
);

const AeroFlightCard: React.FC<AeroFlightCardProps> = ({
    trip,
    isExpanded,
    onClick,
    onSelect,
}) => {
    const flight = trip.tripFlight;
    const lowestFare = getLowestFare(trip);
    const seatsLeft = trip.fareClasses[0]?.avail ?? null;
    const showSeatsLeft = seatsLeft !== null && seatsLeft <= 5 && seatsLeft > 0;

    const firstFareClass = trip.fareClasses[0];
    const firstFare = firstFareClass?.fares[0];
    const firstPtcFare = firstFare?.ptcFares[0];

    const originAirport =
        AERO_AIRPORTS[flight.origin ];
    const destAirport =
        AERO_AIRPORTS[flight.destination ];

    const dateStr = formatDate(flight.departureDate);
    const depTimeStr = formatTime(flight.departureTime);
    const arrTimeStr = formatTime(flight.arrivalTime);
    const durationStr = formatDuration(flight.duration);
    const priceStr = `From $${lowestFare.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    const sharedProps = {
        dateStr,
        priceStr,
        depTime: depTimeStr,
        arrTime: arrTimeStr,
        duration: durationStr,
        originCity: originAirport?.city ?? flight.origin,
        originCode: flight.origin,
        destCity: destAirport?.city ?? flight.destination,
        destCode: flight.destination,
        arrivalDateAdjustor: flight.arrivalDateAdjustor,
        showSeatsLeft,
        seatsLeft,
    };

    if (!isExpanded) {
        /* ── COLLAPSED — clickable card ── */
        return (
            <div
                onClick={onClick}
                className="cursor-pointer"
                style={{
                    border: "1px solid #e9e9e9",
                    borderRadius: 8,
                    padding: 16,
                    marginBottom: 16,
                    backgroundColor: "#fff",
                }}
            >
                <CardContent {...sharedProps} />
            </div>
        );
    }

    /* ── EXPANDED — all inside one bordered card (matches Aero's DOM) ── */
    return (
        <div
            style={{
                border: "1px solid #e9e9e9",
                borderRadius: 8,
                marginBottom: 16,
                overflow: "hidden",
            }}
        >
            {/* Flight info — same compact view */}
            <div style={{ padding: 16 }}>
                <CardContent {...sharedProps} />
            </div>

            {/* "Confirm your fare" */}
            <div
                style={{
                    padding: "8px 16px",
                    fontSize: 16,
                    fontWeight: 400,
                    color: "#000",
                }}
            >
                Confirm your fare
            </div>

            {/* Aero Select fare — bordered card with orange left accent, clickable */}
            {firstFareClass && firstFare && firstPtcFare && (
                <div style={{ padding: "0 8px 8px" }}>
                    <div
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(firstFareClass, firstFare, firstPtcFare);
                        }}
                        className="cursor-pointer transition-all"
                        style={{
                            border: "1px solid #e9e9e9",
                            borderRadius: 8,
                            padding: 16,
                            backgroundColor: "#fff",
                            outline: "4px solid transparent",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "#c6c6c6";
                            e.currentTarget.style.outlineColor = "#f3f3f3";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "#e9e9e9";
                            e.currentTarget.style.outlineColor = "transparent";
                        }}
                    >
                        {/* Inner content with orange left border */}
                        <div
                            style={{
                                borderLeft: "2px solid #fb6900",
                                paddingLeft: 16,
                            }}
                        >
                            <div
                                className="flex items-center justify-between"
                                style={{ marginBottom: 8 }}
                            >
                                <div className="flex items-center gap-2">
                                    <span
                                        style={{
                                            fontSize: 24,
                                            fontWeight: 400,
                                            color: "#000",
                                        }}
                                    >
                                        Aero Select
                                    </span>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 500,
                                            color: "#fff",
                                            backgroundColor: "#fb6900",
                                            padding: "3px 8px",
                                            borderRadius: 4,
                                        }}
                                    >
                                        Updated terms
                                    </span>
                                </div>
                                <span
                                    style={{
                                        fontSize: 16,
                                        fontWeight: 500,
                                        color: "#000",
                                    }}
                                >
                                    $
                                    {lowestFare.toLocaleString("en-US", {
                                        minimumFractionDigits: 0,
                                        maximumFractionDigits: 0,
                                    })}
                                </span>
                            </div>
                            <p
                                style={{
                                    fontSize: 13,
                                    lineHeight: "20px",
                                    color: "#949494",
                                    margin: 0,
                                }}
                            >
                                {FARE_POLICY}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AeroFlightCard;
